<template>
	<div class="content-wrapper">
		<!-- Content -->

		<div class="container flex-grow-1 container-p-y">
			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4" v-if="hasAnyCertificate">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ $t('certificates.title') }}</h4>
					<p class="mb-0">{{ $t('certificates.description') }}</p>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-4">
					<button class="btn btn-primary waves-effect waves-light" type="button" disabled="" v-if="isDownloading">
						<span class="spinner-border me-1" role="status" aria-hidden="true"></span>
						{{ $t('common.loading') }}
					</button>
					<button type="submit" class="btn btn-primary" @click="downloadAllCertificates()" v-if="!isDownloading">{{ $t('certificates.downloadAll') }}</button>
				</div>
			</div>

			<div class="spinner-border spinner-border-lg text-primary load-center" role="status" v-if="isLoading && !hasAnyCertificate && !search">
				<span class="visually-hidden">{{ $t('common.loading') }}</span>
			</div>

			<div class="col-lg-12" v-if="hasAnyCertificate || search">
				<div class="card h-100">
					<div class="card-body pb-0">
						<div class="form-floating form-floating-outline">
							<input
								type="text"
								class="form-control"
								:placeholder="$t('certificates.searchPlaceholder')"
								v-model="search"
								autocomplete="off"
							/>
							<label>{{ $t('certificates.searchPlaceholder') }}</label>
						</div>
					</div>
					<div class="table-responsive">
						<table class="table table-borderless">
							<thead>
								<tr class="border-bottom">
									<th class="bg-transparent fs-xsmall">{{ $t('certificates.number') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('certificates.training') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('certificates.type') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('certificates.version') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('certificates.issuedAt') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('common.fields.actions') }}</th>
								</tr>
							</thead>
							<tbody>
								<template v-if="isLoading">
									<tr v-for="n in 6" :key="`skeleton-${n}`" class="placeholder-glow" aria-hidden="true">
										<td><span class="placeholder rounded col-8"></span></td>
										<td><span class="placeholder rounded-pill col-10"></span></td>
										<td><span class="placeholder rounded col-6"></span></td>
										<td><span class="placeholder rounded col-5"></span></td>
										<td><span class="placeholder rounded col-7"></span></td>
										<td><span class="placeholder rounded col-4"></span></td>
									</tr>
								</template>
								<tr v-else-if="certificates.length === 0">
									<td colspan="6" class="text-center text-muted py-4">
										{{ $t('certificates.noSearchResult') }}
									</td>
								</tr>
								<tr v-for="(certificate, index) in certificates" :key="certificate._id || index">
									<td>
										<div class="d-flex justify-content-start align-items-center">
											<div>
												<h6 class="mb-0 text-truncate">
													{{ certificate.user.firstName }} {{ certificate.user.lastName }}
													<span v-if="certificate.status !== 'succeeded'" style="color:red;">{{ $t('certificates.failedBadge') }}</span>
												</h6>
											</div>
										</div>
										<p class="text-truncate mb-0" v-if="certificate.certificateType === 'dgr' && !certificate.certificateReady && certificate.status === 'succeeded'" style="color: orange;">
											{{ $t('certificates.waitingIATA') }}
										</p>
									</td>
									<td>
										<span class="badge bg-label-info rounded-pill" v-if="certificate.status === 'succeeded'">
											{{ certificate.formationTitle }}
										</span>
										<span class="badge bg-label-danger rounded-pill" v-else>
											{{ certificate.formationTitle }}
										</span>
									</td>
									<td>
										<span 
											v-if="certificate.type === 'renewal'"
										>
											{{ $t('common.trainingTypes.renewal') }}
										</span>
										<span v-else>
											{{ $t('common.trainingTypes.initial') }}
										</span>
									</td>
									<td class="text-truncate">
										{{ certificate.versionName }}
									</td>
									<td>
										{{ formatDate(certificate.finishedAt) }}
									</td>
									<td>
										<a href="javascript:void(0);" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" @click="downloadCertificate(certificate._id)" data-bs-toggle="tooltip" :data-bs-title="$t('certificates.attestation')" v-if="displayDownloadCertificate(certificate)">
											<i class="ri-download-2-line ri-22px"></i>
										</a>

										<span class="badge bg-label-warning rounded-pill mr-5" data-bs-toggle="tooltip" :data-bs-title="$t('certificates.pendingCertificate')" v-if="certificate.certificateType === 'dgr' && !certificate.certificateReady && certificate.status === 'succeeded'">
											<i class="ri-hourglass-line"></i>
										</span>

										<a href="javascript:void(0);" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" @click="downloadCertificateCompletion(certificate._id)" data-bs-toggle="tooltip" :data-bs-title="$t('certificates.completionCertificate')" v-if="displayCompletionCertificate">
											<i class="ri-article-line ri-22px"></i>
										</a>

										<a href="javascript:void(0);" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" @click="downloadEvaluationCertificate(certificate._id)" data-bs-toggle="tooltip" :data-bs-title="$t('certificates.evaluationCertificate')" v-if="displayEvaluationCertificate">
											<i class="ri-survey-line ri-22px"></i>
										</a>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div ref="sentinelRef" class="py-2"></div>
					<div class="text-center py-3" v-if="isLoadingMore">
						<div class="spinner-border spinner-border-sm text-primary" role="status">
							<span class="visually-hidden">{{ $t('common.loading') }}</span>
						</div>
					</div>
				</div>
			</div>

			<div class="card" v-if="!isLoading && certificates.length === 0 && !search">
				<div class="card-body row gy-4">
					<div class="col-sm-12 col-lg-12 text-center px-4">
						<div class="d-flex justify-content-center">
							<div class="avatar avatar-lg mb-4">
								<span class="avatar-initial rounded-3 bg-label-primary">
									<i class="ri-pass-valid-line ri-36px"></i>
								</span>
							</div>
						</div>
						<h4 class="card-title mb-4">{{ $t('certificates.noCertificates') }}</h4>
						<p class="card-text">
							{{ $t('certificates.noCertificatesDescription') }}<br />
							<a href="https://media.pika-aero.com/exemple_attestation_11.2.6.2.pdf" target="_blank">{{ $t('certificates.downloadExample') }}</a>
						</p>
						<router-link to="/trainings/create">
							<button class="btn btn-primary mt-5">{{ $t('certificates.orderFirstTraining') }}</button>
						</router-link>
						<br />
					</div>
				</div>
			</div>
		</div>
		<!-- / Content -->

		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import moment from "moment";
import "moment/locale/fr";

moment.locale(localStorage.getItem('locale') || 'fr');

import { ref, watch, nextTick } from "vue";
import { Tooltip } from 'bootstrap'
import { useInfiniteScroll } from "@/composables/useInfiniteScroll";

export default {
	name: "ConventionPages",

	setup() {
		const search = ref("");
		const debouncedSearch = ref("");
		const displayCompletionCertificate = ref(false);
		const displayEvaluationCertificate = ref(false);
		const hasAnyCertificate = ref(false);

		let debounceTimer = null;
		watch(search, (val) => {
			if (debounceTimer) clearTimeout(debounceTimer);
			debounceTimer = setTimeout(() => {
				debouncedSearch.value = val.trim();
			}, 300);
		});

		const extraParams = ref({ search: "" });
		watch(debouncedSearch, (val) => {
			extraParams.value = { search: val };
		});

		const infinite = useInfiniteScroll({
			limit: 150,
			extraParams,
			fetchPage: async (page, limit, params) => {
				const response = await HTTP.get("/certificates", {
					params: { page, limit, search: params.search || "" }
				});
				if (response.data.displayCompletionCertificate !== undefined)
					displayCompletionCertificate.value = response.data.displayCompletionCertificate;
				if (response.data.displayEvaluationCertificate !== undefined)
					displayEvaluationCertificate.value = response.data.displayEvaluationCertificate;
				const total = response.data.pagination?.totalEntries || 0;
				if (!params.search && total > 0)
					hasAnyCertificate.value = true;
				return {
					items: response.data.certificates,
					totalEntries: total,
					totalPages: response.data.pagination?.totalPages || 0
				};
			}
		});

		watch(infinite.items, async () => {
			await nextTick();
			const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
			tooltipTriggerList.forEach((el) => {
				if (!el._tooltipInitialized) {
					new Tooltip(el);
					el._tooltipInitialized = true;
				}
			});
		});

		return {
			search,
			displayCompletionCertificate,
			displayEvaluationCertificate,
			hasAnyCertificate,
			certificates: infinite.items,
			isLoading: infinite.isLoading,
			isLoadingMore: infinite.isLoadingMore,
			hasMore: infinite.hasMore,
			sentinelRef: infinite.sentinelRef
		};
	},

	data() {
		return {
			isDownloading: false
		};
	},

	methods: {
		displayDownloadCertificate(certificate) {
			if (certificate.status === 'succeeded' && certificate.certificateType !== 'dgr')
				return true;

			if (certificate.status === 'succeeded' && certificate.certificateType === 'dgr' && certificate.certificateReady)
				return true;

			return false;
		},

		async downloadCertificate(trainingId) {
			try {
				// Appel API pour récupérer le PDF en base64
				const response = await HTTP.post(`/certificates/download`, { trainingId });

				if (response.data?.base64) {
					// Convertir la base64 en un Blob et créer un lien de téléchargement
					const byteCharacters = atob(response.data.base64);
					const byteNumbers = new Array(byteCharacters.length);
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.charCodeAt(i);
					}
					const byteArray = new Uint8Array(byteNumbers);
					const blob = new Blob([byteArray], { type: 'application/pdf' });

					// Créer un lien pour le téléchargement
					const link = document.createElement('a');
					link.href = URL.createObjectURL(blob);
					link.download = `attestation_${trainingId}.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					throw new Error("Fichier non disponible.");
				}
			} catch (error) {
				// Gestion des erreurs
				const errorMessage = error?.response?.data?.message || 
					this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async downloadCertificateCompletion(trainingId) {
			try {
				// Appel API pour récupérer le PDF en base64
				const response = await HTTP.post(`/certificates/completion/download`, { trainingId });

				if (response.data?.base64) {
					// Convertir la base64 en un Blob et créer un lien de téléchargement
					const byteCharacters = atob(response.data.base64);
					const byteNumbers = new Array(byteCharacters.length);
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.charCodeAt(i);
					}
					const byteArray = new Uint8Array(byteNumbers);
					const blob = new Blob([byteArray], { type: 'application/pdf' });

					// Créer un lien pour le téléchargement
					const link = document.createElement('a');
					link.href = URL.createObjectURL(blob);
					link.download = `certificat_completion_${trainingId}.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					throw new Error("Fichier non disponible.");
				}
			} catch (error) {
				// Gestion des erreurs
				const errorMessage = error?.response?.data?.message ||
					this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async downloadEvaluationCertificate(trainingId) {
			try {
				// Appel API pour récupérer le PDF en base64
				const response = await HTTP.post(`/certificates/evaluation/download`, { trainingId });

				if (response.data?.base64) {
					// Convertir la base64 en un Blob et créer un lien de téléchargement
					const byteCharacters = atob(response.data.base64);
					const byteNumbers = new Array(byteCharacters.length);
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.charCodeAt(i);
					}
					const byteArray = new Uint8Array(byteNumbers);
					const blob = new Blob([byteArray], { type: 'application/pdf' });

					// Créer un lien pour le téléchargement
					const link = document.createElement('a');
					link.href = URL.createObjectURL(blob);
					link.download = `certificat_questions_${trainingId}.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					throw new Error("Fichier non disponible.");
				}
			} catch (error) {
				// Gestion des erreurs
				const errorMessage = error?.response?.data?.message ||
					this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async downloadAllCertificates() {
			try {
				this.isDownloading = true;

				// Appel API pour récupérer le PDF en base64
				const response = await HTTP.post(`/certificates/download-all`);

				// Télécharger le fichier depuis l'URL signée
				const link = document.createElement('a');
				link.href = response.data.downloadUrl;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				this.isDownloading = false;
			} catch (error) {
				// Gestion des erreurs
				const errorMessage = error?.response?.data?.message ||
					this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		formatDate(date) {
			return moment(date).format("DD/MM/YYYY"); // Affiche "il y a 3 semaines"
		},

		formatRelativeDate(date) {
			return moment(date).fromNow(); // Affiche "il y a 3 semaines"
		},
	}
};
</script>

<style scoped>
/* Ajoutez des styles spécifiques à la page Dashboard */
.dashboard {
	padding: 1rem;
}

.card-title {
	font-size: 1.2rem;
}

.card-text {
	margin: 0;
}

.load-center {
	display: block;
	margin: 0 auto;
}

.text-truncate {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 250px;
}
</style>
